/**
 * DataRoomCollaborators — Session 1 · admins manage who can upload +
 * innovator team members (owner/editor) see their own access level.
 *
 * Corresponds to:
 *   GET    /api/data-room/:biotech_id/collaborators
 *   POST   /api/data-room/:biotech_id/collaborators   {email, role, name?}
 *   PATCH  /api/data-room/:biotech_id/collaborators/:id  {role}
 *   DELETE /api/data-room/:biotech_id/collaborators/:id
 *   GET    /api/data-room/:biotech_id/activity
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, ShieldCheck, X as IconX, Loader2, Clock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABEL = {
  admin:  { label: 'Administrator',  tone: 'text-indigo-700 bg-indigo-50 border-indigo-200' },
  owner:  { label: 'Company Owner',  tone: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  editor: { label: 'Editor',         tone: 'text-sky-700 bg-sky-50 border-sky-200' },
  viewer: { label: 'Viewer',         tone: 'text-slate-700 bg-slate-50 border-slate-200' },
};

const ACTION_LABEL = {
  upload: 'uploaded',
  delete: 'deleted',
  rename: 'edited metadata on',
  invite: 'invited',
  collaborator_role_change: 'changed role for',
  collaborator_revoked: 'revoked access for',
  accept_invite: 'joined as',
  buyer_open: 'opened data room',
  buyer_download: 'downloaded',
  buyer_zip_download: 'downloaded all files as ZIP',
};

export function DataRoomCollaborators({ biotechId }) {
  const [state, setState] = useState({ my_role: '', collaborators: [] });
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');
  const [inviting, setInviting] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);

  const load = useCallback(async () => {
    if (!biotechId) return;
    setLoading(true);
    try {
      const [c, a] = await Promise.all([
        axios.get(`${API}/data-room/${biotechId}/collaborators`, { withCredentials: true }),
        axios.get(`${API}/data-room/${biotechId}/activity?limit=30`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
      ]);
      setState(c.data);
      setActivity(a.data.items || []);
    } catch (e) {
      // 403 is expected for non-collaborators — surface a friendly hint.
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || 'Could not load collaborators');
      }
    } finally {
      setLoading(false);
    }
  }, [biotechId]);

  useEffect(() => { load(); }, [load]);

  const canManage = state.my_role === 'admin';

  const invite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    setInviting(true);
    try {
      await axios.post(
        `${API}/data-room/${biotechId}/collaborators`,
        { email, name: inviteName.trim() || undefined, role: inviteRole },
        { withCredentials: true },
      );
      toast.success(`Invited ${email} as ${ROLE_LABEL[inviteRole].label}`);
      setInviteEmail(''); setInviteName(''); setInviteRole('editor');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Invite failed');
    } finally {
      setInviting(false);
    }
  };

  const changeRole = async (collab, newRole) => {
    if (collab.role === newRole) return;
    try {
      await axios.patch(
        `${API}/data-room/${biotechId}/collaborators/${collab.id}`,
        { role: newRole },
        { withCredentials: true },
      );
      toast.success(`${collab.email} is now ${ROLE_LABEL[newRole].label}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Role change failed');
    }
  };

  const revoke = async (collab) => {
    if (!window.confirm(`Revoke ${collab.email}'s access to this Data Room?`)) return;
    try {
      await axios.delete(
        `${API}/data-room/${biotechId}/collaborators/${collab.id}`,
        { withCredentials: true },
      );
      toast.success(`Revoked ${collab.email}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Revoke failed');
    }
  };

  const resend = async (collab) => {
    try {
      const r = await axios.post(
        `${API}/data-room/${biotechId}/collaborators/${collab.id}/resend`,
        {},
        { withCredentials: true },
      );
      if (r.data?.ok) {
        toast.success(`Invite re-sent to ${collab.email}`);
      } else if (r.data?.accept_url) {
        // Gmail send failed — offer the raw link as a fallback.
        await navigator.clipboard.writeText(r.data.accept_url).catch(() => {});
        toast.warning('Email send failed. Invite link copied to clipboard — share manually.');
      } else {
        toast.error(r.data?.error || 'Resend failed');
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    }
  };

  const copyInviteLink = async (collab) => {
    try {
      // Trigger resend just to get the URL back (server returns it in
      // both success and failure cases).
      const r = await axios.post(
        `${API}/data-room/${biotechId}/collaborators/${collab.id}/resend`,
        {},
        { withCredentials: true },
      );
      const url = r.data?.accept_url;
      if (url) {
        await navigator.clipboard.writeText(url);
        toast.success('Invite link copied to clipboard');
      }
    } catch (e) {
      toast.error('Could not fetch invite link');
    }
  };

  if (loading) return <div className="text-[11px] text-slate-500 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading collaborators…</div>;
  if (!state.my_role) return null; // Access-denied is silent for non-collaborators.

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3.5 space-y-3" data-testid="data-room-collaborators">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-indigo-600" />
          <div>
            <div className="text-[12px] font-semibold text-slate-800">Data Room collaborators</div>
            <div className="text-[10px] text-slate-500">
              Your role · <span className={`inline-block px-1.5 py-[1px] rounded border ${ROLE_LABEL[state.my_role]?.tone || ''}`}>{ROLE_LABEL[state.my_role]?.label || state.my_role}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setActivityOpen(v => !v)}
          className="text-[11px] font-medium text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          data-testid="data-room-activity-toggle"
        >
          <Clock className="w-3 h-3" /> Activity log ({activity.length})
        </button>
      </div>

      {/* Collaborator list */}
      {state.collaborators.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="data-room-collab-list">
          {state.collaborators.map(c => {
            const rl = ROLE_LABEL[c.role] || ROLE_LABEL.viewer;
            return (
              <li key={c.id} className="py-1.5 flex items-center gap-2 text-[12px]" data-testid={`collab-${c.email}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                    {c.name || c.email}
                    {c.invite_accepted && (
                      <span
                        className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"
                        title={`Joined on ${c.accepted_at ? new Date(c.accepted_at).toLocaleString() : 'a while ago'}`}
                        data-testid={`collab-accepted-dot-${c.email}`}
                      />
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {c.email}
                    {' · '}
                    {c.invite_accepted ? (
                      <span className="text-emerald-700" data-testid={`collab-status-${c.email}`}>
                        Joined{c.accepted_at ? ` ${new Date(c.accepted_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}
                      </span>
                    ) : (
                      <span className="text-amber-700" data-testid={`collab-status-${c.email}`}>invite pending</span>
                    )}
                  </div>
                </div>
                {canManage ? (
                  <>
                    {!c.invite_accepted && (
                      <>
                        <button
                          type="button"
                          onClick={() => resend(c)}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 font-medium"
                          title="Send the invite email again"
                          data-testid={`collab-resend-${c.email}`}
                        >
                          Send invite
                        </button>
                        <button
                          type="button"
                          onClick={() => copyInviteLink(c)}
                          className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          title="Copy the accept URL to share manually"
                          data-testid={`collab-copylink-${c.email}`}
                        >
                          Copy link
                        </button>
                      </>
                    )}
                    <select
                      value={c.role}
                      onChange={e => changeRole(c, e.target.value)}
                      className="text-[11px] px-1.5 py-0.5 rounded border border-slate-200 bg-white"
                      data-testid={`collab-role-${c.email}`}
                    >
                      <option value="owner">Owner</option>
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => revoke(c)}
                      className="text-slate-400 hover:text-rose-600"
                      title="Revoke access"
                      data-testid={`collab-revoke-${c.email}`}
                    >
                      <IconX className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <span className={`text-[10px] px-1.5 py-[1px] rounded border ${rl.tone}`}>{rl.label}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Invite form — admin only */}
      {canManage && (
        <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-slate-100" data-testid="data-room-invite-form">
          <input
            type="email"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            placeholder="collaborator@company.com"
            className="flex-1 min-w-[180px] text-[12px] px-2 py-1 rounded border border-slate-200 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-100"
            data-testid="invite-email"
          />
          <input
            type="text"
            value={inviteName}
            onChange={e => setInviteName(e.target.value)}
            placeholder="Name (optional)"
            className="w-32 text-[12px] px-2 py-1 rounded border border-slate-200"
            data-testid="invite-name"
          />
          <select
            value={inviteRole}
            onChange={e => setInviteRole(e.target.value)}
            className="text-[12px] px-2 py-1 rounded border border-slate-200 bg-white"
            data-testid="invite-role"
          >
            <option value="owner">Owner (full innovator control)</option>
            <option value="editor">Editor (upload + edit)</option>
            <option value="viewer">Viewer (read-only)</option>
          </select>
          <button
            type="button"
            onClick={invite}
            disabled={inviting || !inviteEmail.trim()}
            className="inline-flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-[12px] font-semibold px-3 py-1 disabled:opacity-60"
            data-testid="invite-submit"
          >
            {inviting ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
            Invite
          </button>
        </div>
      )}

      {/* Activity log — collapsible below */}
      {activityOpen && (
        <div className="border-t border-slate-100 pt-2 max-h-56 overflow-y-auto" data-testid="data-room-activity-list">
          {activity.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No activity yet.</div>
          ) : (
            <ul className="space-y-1 text-[11px]">
              {activity.map(a => {
                const t = new Date(a.at || Date.now());
                const when = t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                const who = a.actor_name || a.actor_email || 'Someone';
                const what = ACTION_LABEL[a.action] || a.action;
                const target = a.target?.filename || a.target?.email || '';
                return (
                  <li key={a.id} className="text-slate-700 flex items-center gap-2">
                    <span className="text-slate-400 tabular-nums">{when}</span>
                    <span className="font-medium text-slate-800 truncate">{who}</span>
                    <span className="text-slate-500">{what}</span>
                    {target && <span className="text-indigo-700 truncate">{target}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default DataRoomCollaborators;
